import { useMemo } from "react";
import { fillSlots } from "@hightouch/lib/ad-studio/templates/editor-slots";

import {
  buildAdTemplateExampleValues,
  parseTemplateAssets,
  parseTemplateManifest,
  parseTemplateStringMap,
} from "src/pages/ad-studio/templates/types";
import { useRegionConfig } from "src/utils/use-region-config";

type TemplateContent = {
  editorHtml?: string | null;
  html?: string | null;
  manifest?: unknown;
  assets?: unknown;
  originalSampleValues?: unknown;
};

/**
 * The template's markup with every slot filled by its authored sample value —
 * what a template actually looks like. Slots are stored empty, so rendering the
 * saved markup directly would show holes where the content belongs.
 *
 * Returns null when the content carries no markup or no parseable manifest.
 */
export function useFilledTemplateHtml(content: TemplateContent): {
  html: string | null;
  assets: Record<string, string>;
} {
  const { apiEndpoint } = useRegionConfig();
  const assets = useMemo(
    () => parseTemplateAssets(content.assets),
    [content.assets],
  );
  const originalSampleValues = useMemo(
    () => parseTemplateStringMap(content.originalSampleValues),
    [content.originalSampleValues],
  );
  const markup = content.editorHtml ?? content.html ?? null;

  const html = useMemo(() => {
    if (!markup) return null;
    const manifest = parseTemplateManifest(content.manifest);
    if (!manifest) return null;
    return fillSlots(
      markup,
      manifest,
      buildAdTemplateExampleValues({
        manifest,
        originalSampleValues,
        assets,
        apiBase: apiEndpoint,
      }),
    );
  }, [apiEndpoint, assets, content.manifest, markup, originalSampleValues]);

  return { html, assets };
}
